// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import {
  Component,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCircleCheck,
  IconDeviceFloppy,
  IconHistory,
  IconPencil,
  IconShieldCheck,
  IconUpload,
} from "@tabler/icons-react";

import { api, PayrollApiError } from "../../lib/api";
import { queryClient } from "../../lib/queryClient";
import { Button, Modal } from "../ui";

type PayslipField = {
  raw: string;
  extractedValue: unknown;
  effectiveValue: unknown;
  unit: "text" | "days" | "hours" | "money" | "percent";
  source: "pdf_text" | "manual";
  correctedAt?: string;
  correctedBy?: string;
};

type PayslipDocument = {
  schemaVersion: 1;
  parserVersion: string;
  identity: Record<string, PayslipField>;
  period: Record<string, PayslipField>;
  workAndAbsence: Record<string, PayslipField>;
  earnings: Record<string, PayslipField>;
  deductionsAndTax: Record<string, PayslipField>;
  employerContributions: Record<string, PayslipField>;
  totals: Record<string, PayslipField>;
  review?: {
    revision: number;
    confirmedAt: string | null;
    confirmedBy: string | null;
  };
};

type PayslipDetail = {
  id: number;
  month: string;
  pdfFileName: string;
  employee: {
    id: number;
    displayName: string;
    payrollEmployeeCode: string;
  } | null;
  parsedEmployeeName: string;
  parsedPayrollCode?: string;
  parsedPeriod?: string;
  matchStatus?: string;
  validationCodes?: string[];
  warnings?: string[];
  requiresReplacement?: boolean;
  correctionRevision?: number;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
  payslipDocument?: PayslipDocument;
  calculationReady: boolean;
  payslipAvailable: boolean;
  pdfLayout: {
    width: number;
    height: number;
    glyphs: Array<{
      text: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
  };
};

const payslipReview = (detail: PayslipDetail) =>
  detail.payslipDocument?.review ?? {
    revision: detail.correctionRevision ?? 0,
    confirmedAt: detail.confirmedAt ?? null,
    confirmedBy: detail.confirmedBy ?? null,
  };

const payslipField = (
  document: PayslipDocument | undefined,
  fieldRef: FieldRef,
): PayslipField =>
  document?.[fieldRef.section]?.[fieldRef.name] ?? emptyField(fieldRef.short);

type PayslipAuditEvent = {
  id: number;
  actorDisplayName: string;
  action: string;
  beforePayload: Record<string, unknown>;
  afterPayload: Record<string, unknown>;
  reason: string;
  createdAt: string;
};

type SectionKey = Exclude<
  keyof PayslipDocument,
  "schemaVersion" | "parserVersion" | "review"
>;

type FieldRef = {
  section: SectionKey;
  name: string;
  short?: string;
};

type GridCell = {
  label: string;
  fields: FieldRef[];
  emphasis?: "brand" | "total";
};

const ref = (section: SectionKey, name: string, short?: string): FieldRef => ({
  section,
  name,
  short,
});

const identityColumns: GridCell[][] = [
  [
    {
      label: "Zamestnávateľ",
      fields: [ref("identity", "employerName")],
      emphasis: "brand",
    },
    { label: "Obdobie", fields: [ref("period", "period")] },
    { label: "Meno", fields: [ref("identity", "employeeName")] },
    { label: "Osobné číslo", fields: [ref("identity", "payrollCode")] },
    { label: "ZP", fields: [ref("identity", "healthInsuranceProvider")] },
  ],
  [
    {
      label: "Sadzba",
      fields: [
        ref("period", "wageRateType", "typ"),
        ref("period", "wageRate", "€"),
      ],
    },
    { label: "Priemerný zárobok", fields: [ref("period", "averageEarnings")] },
    {
      label: "Zvyšok dovolenky",
      fields: [ref("workAndAbsence", "remainingVacationDays", "d")],
    },
  ],
  [
    { label: "Pracovný pomer", fields: [ref("identity", "employmentType")] },
    {
      label: "Prac. / Sviatky / Úväzok",
      fields: [
        ref("workAndAbsence", "scheduledWorkDays", "d"),
        ref("workAndAbsence", "scheduledHolidayDays", "d"),
        ref("workAndAbsence", "nominalHours", "h"),
      ],
    },
    {
      label: "Odpracované",
      fields: [
        ref("workAndAbsence", "workedDays", "d"),
        ref("workAndAbsence", "paidHours", "h"),
      ],
    },
    {
      label: "Choroba / OČR / MD",
      fields: [
        ref("workAndAbsence", "illnessDays", "d"),
        ref("workAndAbsence", "familyCareDays", "d"),
        ref("workAndAbsence", "parentalLeaveDays", "d"),
      ],
    },
    {
      label: "Neospr. / Nepl. voľno / Štrajk",
      fields: [
        ref("workAndAbsence", "unexcusedAbsenceDays", "d"),
        ref("workAndAbsence", "unpaidLeaveDays", "d"),
        ref("workAndAbsence", "strikeDays", "d"),
      ],
    },
  ],
];

const earningsColumns: GridCell[][] = [
  [
    { label: "Základná mzda", fields: [ref("earnings", "baseWage", "€")] },
    {
      label: "Dovolenka",
      fields: [
        ref("workAndAbsence", "vacationDays", "d"),
        ref("workAndAbsence", "vacationHours", "h"),
        ref("earnings", "vacationCompensation", "€"),
      ],
    },
    {
      label: "Sviatky",
      fields: [
        ref("workAndAbsence", "publicHolidayDays", "d"),
        ref("workAndAbsence", "publicHolidayHours", "h"),
        ref("earnings", "publicHolidayCompensation", "€"),
      ],
    },
    {
      label: "Platené voľno",
      fields: [
        ref("workAndAbsence", "paidLeaveDays", "d"),
        ref("workAndAbsence", "paidLeaveHours", "h"),
        ref("earnings", "paidLeaveCompensation", "€"),
      ],
    },
    {
      label: "Hrubá mzda",
      fields: [ref("earnings", "grossWage", "€")],
      emphasis: "total",
    },
  ],
  [
    {
      label: "Osobné ohodnotenie",
      fields: [ref("earnings", "personalBonus", "€")],
    },
    { label: "Prémie", fields: [ref("earnings", "bonuses", "€")] },
    {
      label: "Prémie za dlhšie obdobie",
      fields: [ref("earnings", "longTermBonus", "€")],
    },
  ],
  [
    {
      label: "Práca v noci",
      fields: [
        ref("earnings", "nightWorkHours", "h"),
        ref("earnings", "nightWorkAmount", "€"),
      ],
    },
    {
      label: "Práca v sobotu",
      fields: [
        ref("earnings", "saturdayWorkHours", "h"),
        ref("earnings", "saturdayWorkAmount", "€"),
      ],
    },
    {
      label: "Práca v nedeľu",
      fields: [
        ref("earnings", "sundayWorkHours", "h"),
        ref("earnings", "sundayWorkAmount", "€"),
      ],
    },
  ],
];

const deductionColumns: GridCell[][] = [
  [
    {
      label: "Nemocenské",
      fields: [ref("deductionsAndTax", "sicknessInsurance", "€")],
    },
    {
      label: "Poist. v nezam.",
      fields: [ref("deductionsAndTax", "unemploymentInsurance", "€")],
    },
    {
      label: "Nezdaniteľná časť",
      fields: [ref("deductionsAndTax", "taxFreeAllowance", "€")],
    },
    {
      label: "Peň. osl. príjmy",
      fields: [ref("deductionsAndTax", "monetaryExemptIncome", "€")],
    },
  ],
  [
    {
      label: "Starobné",
      fields: [ref("deductionsAndTax", "oldAgeInsurance", "€")],
    },
    {
      label: "Zdravotné",
      fields: [ref("deductionsAndTax", "healthInsurance", "€")],
    },
    { label: "Daň", fields: [ref("deductionsAndTax", "incomeTax", "€")] },
    {
      label: "Nepeň. osl. príjmy",
      fields: [ref("deductionsAndTax", "nonMonetaryExemptIncome", "€")],
    },
  ],
  [
    {
      label: "Invalidné",
      fields: [ref("deductionsAndTax", "disabilityInsurance", "€")],
    },
    {
      label: "Daňový bonus - deti",
      fields: [ref("deductionsAndTax", "childTaxBonus", "€")],
    },
  ],
];

const employerCells: GridCell[] = [
  {
    label: "NP",
    fields: [ref("employerContributions", "employerSicknessInsurance", "€")],
  },
  {
    label: "SP",
    fields: [ref("employerContributions", "employerOldAgeInsurance", "€")],
  },
  {
    label: "IP",
    fields: [ref("employerContributions", "employerDisabilityInsurance", "€")],
  },
  {
    label: "PvN",
    fields: [
      ref("employerContributions", "employerUnemploymentInsurance", "€"),
    ],
  },
  {
    label: "UP",
    fields: [ref("employerContributions", "employerAccidentInsurance", "€")],
  },
  {
    label: "GP",
    fields: [ref("employerContributions", "employerGuaranteeInsurance", "€")],
  },
  {
    label: "PRFS",
    fields: [ref("employerContributions", "employerReserveFund", "€")],
  },
  {
    label: "ZP",
    fields: [ref("employerContributions", "employerHealthInsurance", "€")],
  },
];

const summaryCells: GridCell[] = [
  {
    label: "Čistá mzda",
    fields: [ref("totals", "netWage", "€")],
    emphasis: "total",
  },
  {
    label: "Náhrada príjmu",
    fields: [ref("deductionsAndTax", "incomeCompensation", "€")],
  },
  {
    label: "Nezdaniteľné náhrady",
    fields: [ref("deductionsAndTax", "nonTaxableCompensation", "€")],
  },
];

const finalCells: GridCell[] = [
  {
    label: "Vyúčtovanie",
    fields: [ref("totals", "settlementAmount", "€")],
    emphasis: "total",
  },
  {
    label: "Celková cena práce",
    fields: [ref("totals", "totalLabourCost", "€")],
    emphasis: "total",
  },
];

const allGridFieldRefs = [
  ...identityColumns.flatMap((column) => column.flatMap((cell) => cell.fields)),
  ...earningsColumns.flatMap((column) => column.flatMap((cell) => cell.fields)),
  ...deductionColumns.flatMap((column) =>
    column.flatMap((cell) => cell.fields),
  ),
  ...employerCells.flatMap((cell) => cell.fields),
  ...summaryCells.flatMap((cell) => cell.fields),
  ...finalCells.flatMap((cell) => cell.fields),
].filter(
  (field, index, values) =>
    values.findIndex(
      (candidate) =>
        candidate.section === field.section && candidate.name === field.name,
    ) === index,
);

const fieldLabels: Record<string, string> = Object.fromEntries(
  [...identityColumns, ...earningsColumns, ...deductionColumns]
    .flatMap((column) => column)
    .concat(employerCells, summaryCells, finalCells)
    .flatMap((cell) =>
      cell.fields.map((field) => [
        field.name,
        field.short ? `${cell.label} (${field.short})` : cell.label,
      ]),
    ),
);

const payslipActionErrorMessage = (error: unknown) => {
  if (error instanceof PayrollApiError) {
    if (error.code === "payslip_not_resolvable")
      return "This PDF failed a safety check and cannot be confirmed. Upload a replacement PDF.";
    if (error.code === "payslip_not_confirmable")
      return "Resolve the highlighted employee or period problem before confirming this payslip.";
    if (
      ["payslip_state_changed", "payslip_revision_conflict"].includes(
        error.code,
      )
    )
      return "This payslip changed while it was open. The latest version has been reloaded; review it and try again.";
    if (error.status === 409)
      return error.message && error.message !== "Conflict"
        ? error.message
        : "This payslip cannot be saved from its current state. Review the highlighted action and try again.";
  }
  return error instanceof Error && error.message !== "Conflict"
    ? error.message
    : "The payslip could not be saved. Reload it and try again.";
};

const optionalBlankFieldNames = new Set([
  "paidLeaveDays",
  "paidLeaveHours",
  "paidLeaveCompensation",
]);

const displayValue = (
  value: unknown,
  unit: PayslipField["unit"],
  fieldName?: string,
) => {
  if (value === null || value === undefined || value === "")
    return fieldName && optionalBlankFieldNames.has(fieldName)
      ? "Not provided"
      : "Not extracted";
  if (unit === "text") {
    if (value === "monthly") return "Mesačná";
    if (value === "hourly") return "Hodinová";
    return String(value);
  }
  const number =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(number)) return String(value);
  const formatted = new Intl.NumberFormat("sk-SK", {
    minimumFractionDigits: unit === "money" ? 2 : 0,
    maximumFractionDigits: unit === "money" ? 2 : 4,
  }).format(number);
  if (unit === "days") return `${formatted} d`;
  if (unit === "hours") return `${formatted} h`;
  if (unit === "money") return `${formatted} €`;
  if (unit === "percent") return `${formatted} %`;
  return formatted;
};

const emptyField = (short?: string): PayslipField => ({
  raw: "",
  extractedValue: null,
  effectiveValue: null,
  unit:
    short === "d"
      ? "days"
      : short === "h"
        ? "hours"
        : short === "€"
          ? "money"
          : "text",
  source: "pdf_text",
});

type ExtractionState =
  | "extracted"
  | "optional"
  | "missing"
  | "failed"
  | "corrected";

const extractionState = (
  field: PayslipField,
  fieldName?: string,
): ExtractionState => {
  if (field.source === "manual") return "corrected";
  if (
    field.extractedValue !== null &&
    field.extractedValue !== undefined &&
    field.extractedValue !== ""
  )
    return "extracted";
  if (fieldName && optionalBlankFieldNames.has(fieldName)) return "optional";
  return field.raw.trim() ? "failed" : "missing";
};

const extractionStateLabel: Record<ExtractionState, string> = {
  extracted: "Extracted from PDF text",
  optional: "Optional value was not present on this payslip",
  missing: "Value was not found",
  failed: "Source text was found but the value could not be parsed",
  corrected: "Corrected manually",
};

const PayslipPreview = ({
  title,
  imageUrl,
}: {
  title: string;
  imageUrl: string;
}) => <img className="payslip-sheet__page" src={imageUrl} alt={title} />;

const PayslipGridCell = ({
  cell,
  document,
  editing,
  editable,
  onInspect,
}: {
  cell: GridCell;
  document: PayslipDocument | undefined;
  editing: { section: SectionKey; field: string } | null;
  editable: boolean;
  onInspect: (section: SectionKey, name: string, field: PayslipField) => void;
}) => (
  <div className="payslip-grid__cell" data-emphasis={cell.emphasis ?? "none"}>
    <span className="payslip-grid__label">{cell.label}</span>
    <div className="payslip-grid__values">
      {cell.fields.map((fieldRef) => {
        const field = payslipField(document, fieldRef);
        const state = extractionState(field, fieldRef.name);
        const selected =
          editing?.section === fieldRef.section &&
          editing.field === fieldRef.name;
        const content = (
          <>
            <span>
              {displayValue(field.effectiveValue, field.unit, fieldRef.name)}
            </span>
            {fieldRef.short && field.unit === "text" && (
              <small>{fieldRef.short}</small>
            )}
          </>
        );
        return editable ? (
          <button
            key={`${fieldRef.section}.${fieldRef.name}`}
            type="button"
            className="payslip-grid__value"
            data-state={state}
            data-selected={selected ? "true" : "false"}
            aria-label={`View audit history for ${fieldLabels[fieldRef.name] ?? cell.label}. ${extractionStateLabel[state]}.`}
            title={`${extractionStateLabel[state]}${field.raw ? `\nSource: ${field.raw}` : ""}`}
            onClick={() => onInspect(fieldRef.section, fieldRef.name, field)}
          >
            {content}
          </button>
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={() => onInspect(fieldRef.section, fieldRef.name, field)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onInspect(fieldRef.section, fieldRef.name, field);
              }
            }}
            key={`${fieldRef.section}.${fieldRef.name}`}
            className="payslip-grid__value"
            data-state={state}
            title={`${extractionStateLabel[state]}${field.raw ? `\nSource: ${field.raw}` : ""}`}
          >
            {content}
          </span>
        );
      })}
    </div>
  </div>
);

const CellColumn = (
  props: Omit<Parameters<typeof PayslipGridCell>[0], "cell"> & {
    cells: GridCell[];
  },
) => (
  <div className="payslip-grid__column">
    {props.cells.map((cell) => (
      <PayslipGridCell key={cell.label} {...props} cell={cell} />
    ))}
  </div>
);

const PayslipDataGrid = ({
  detail,
  editing,
  editable,
  onInspect,
}: {
  detail: PayslipDetail;
  editing: { section: SectionKey; field: string } | null;
  editable: boolean;
  onInspect: (section: SectionKey, name: string, field: PayslipField) => void;
}) => {
  const counts = useMemo(() => {
    const result = {
      extracted: 0,
      optional: 0,
      missing: 0,
      failed: 0,
      corrected: 0,
    };
    for (const fieldRef of allGridFieldRefs) {
      const field = payslipField(detail.payslipDocument, fieldRef);
      result[extractionState(field, fieldRef.name)] += 1;
    }
    return result;
  }, [detail]);
  const shared = {
    document: detail.payslipDocument,
    editing,
    editable,
    onInspect,
  };

  return (
    <div className="payslip-grid" aria-label="Extracted payslip values">
      <header className="payslip-grid__header">
        <div>
          <strong>Extracted values</strong>
          <span>
            {editable
              ? "Click any cell to correct it. Hover to see the source text."
              : "Select a value to view its change history. Hover to see the source text."}
          </span>
        </div>
        <div className="payslip-grid__legend" aria-label="Extraction status">
          {counts.extracted > 0 && (
            <span data-state="extracted">
              <IconCircleCheck size={14} />
              {counts.extracted} extracted
            </span>
          )}
          {counts.missing > 0 && (
            <span data-state="missing">
              <IconAlertTriangle size={14} />
              {counts.missing} not found
            </span>
          )}
          {counts.optional > 0 && (
            <span data-state="optional">
              <IconCircleCheck size={14} />
              {counts.optional} optional blanks
            </span>
          )}
          {counts.failed > 0 && (
            <span data-state="failed">
              <IconAlertTriangle size={14} />
              {counts.failed} failed
            </span>
          )}
          {counts.corrected > 0 && (
            <span data-state="corrected">
              <IconPencil size={14} />
              {counts.corrected} corrected
            </span>
          )}
        </div>
      </header>
      <div className="payslip-grid__section payslip-grid__section--three">
        {identityColumns.map((cells, index) => (
          <CellColumn key={index} {...shared} cells={cells} />
        ))}
      </div>
      <div className="payslip-grid__section payslip-grid__section--three">
        {earningsColumns.map((cells, index) => (
          <CellColumn key={index} {...shared} cells={cells} />
        ))}
      </div>
      <div className="payslip-grid__section payslip-grid__section--three">
        {deductionColumns.map((cells, index) => (
          <CellColumn key={index} {...shared} cells={cells} />
        ))}
      </div>
      <div className="payslip-grid__section payslip-grid__summary">
        {summaryCells.map((cell) => (
          <PayslipGridCell key={cell.label} {...shared} cell={cell} />
        ))}
      </div>
      <div className="payslip-grid__contributions">
        <strong>Poistné zamestnávateľa</strong>
        {employerCells.map((cell) => (
          <PayslipGridCell key={cell.label} {...shared} cell={cell} />
        ))}
      </div>
      <div className="payslip-grid__section payslip-grid__final">
        {finalCells.map((cell) => (
          <PayslipGridCell key={cell.label} {...shared} cell={cell} />
        ))}
      </div>
    </div>
  );
};

type PayslipSheetProps = {
  recordId: number;
  mode: "edit" | "read";
  onClose: () => void;
  onUpdated?: () => void | Promise<void>;
  onReplace?: (() => void) | undefined;
};

const PayslipSheetContent = ({
  recordId,
  mode,
  onClose,
  onUpdated,
  onReplace,
}: PayslipSheetProps) => {
  const [valuesExpanded, setValuesExpanded] = useState(false);
  const [editing, setEditing] = useState<{
    section: SectionKey;
    field: string;
  } | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [reason, setReason] = useState("");
  const [auditField, setAuditField] = useState<{
    section: SectionKey;
    field: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    setValuesExpanded(false);
    setEditing(null);
    setAuditField(null);
    setDraftValue("");
    setReason("");
  }, [recordId, mode]);

  const detailQuery = useQuery({
    queryKey: ["openproject", "payslip", recordId],
    queryFn: () =>
      api.getOpenProjectPayslip(recordId) as Promise<PayslipDetail>,
  });
  const previewQuery = useQuery({
    queryKey: ["openproject", "payslip-preview", recordId],
    queryFn: () => api.viewOpenProjectPayslipPreview(recordId),
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const auditQuery = useQuery({
    queryKey: [
      "openproject",
      "payslip-audit",
      recordId,
      detailQuery.data?.employee?.id,
    ],
    queryFn: () =>
      api.getOpenProjectPayrollAudit({
        userId: detailQuery.data?.employee?.id,
        fromMonth: detailQuery.data?.month,
        throughMonth: detailQuery.data?.month,
        technical: true,
      }) as Promise<{ events: PayslipAuditEvent[] }>,
    enabled: Boolean(detailQuery.data?.employee?.id && detailQuery.data?.month),
  });
  useEffect(() => {
    const blob = previewQuery.data?.blob;
    if (!blob) {
      setPreviewUrl(null);
      return;
    }
    const url = window.URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => window.URL.revokeObjectURL(url);
  }, [previewQuery.data?.blob]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!editing || !detailQuery.data)
        throw new Error("Choose a field to correct.");
      const field =
        detailQuery.data.payslipDocument?.[editing.section]?.[editing.field];
      if (!field)
        throw new Error(
          "The selected field is no longer available. Reload the payslip.",
        );
      const value =
        field.unit === "text"
          ? draftValue
          : draftValue.trim() === ""
            ? null
            : Number(draftValue.replace(",", "."));
      if (typeof value === "number" && !Number.isFinite(value))
        throw new Error("Enter a valid number.");
      return api.correctOpenProjectPayslipField(recordId, {
        section: editing.section,
        field: editing.field,
        value,
        expectedRevision: payslipReview(detailQuery.data).revision,
        reason: reason.trim(),
      }) as Promise<PayslipDetail>;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["openproject", "payslip", recordId], data);
      setEditing(null);
      setReason("");
      await onUpdated?.();
    },
    onError: async (error) => {
      if (error instanceof PayrollApiError && error.status === 409)
        await detailQuery.refetch();
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      const current = detailQuery.data;
      if (!current) throw new Error("The payslip is still loading.");
      if (current.requiresReplacement)
        throw new Error(
          "This PDF must be replaced before it can be confirmed.",
        );
      if (
        !["matched", "manual_resolved"].includes(
          current.matchStatus ?? "matched",
        )
      )
        throw new Error(
          "Resolve the highlighted employee or period problem before confirming this payslip.",
        );
      return api.confirmOpenProjectPayslip(
        recordId,
        payslipReview(current).revision,
      ) as Promise<PayslipDetail>;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["openproject", "payslip", recordId], data);
      await onUpdated?.();
    },
    onError: async (error) => {
      if (error instanceof PayrollApiError && error.status === 409)
        await detailQuery.refetch();
    },
  });

  const detail = detailQuery.data;
  const review = detail ? payslipReview(detail) : null;
  const title =
    detail?.employee?.displayName || detail?.parsedEmployeeName || "Payslip";
  const validationCodes = detail?.validationCodes ?? [];
  const identityNeedsAttention = Boolean(
    detail &&
    (validationCodes.includes("employee_unmatched") ||
      detail.matchStatus === "unmatched"),
  );
  const periodNeedsAttention = Boolean(
    detail &&
    (validationCodes.includes("period_mismatch") ||
      detail.matchStatus === "period_mismatch"),
  );
  const requiresReplacement = Boolean(
    detail?.requiresReplacement ||
    validationCodes.some((code) =>
      [
        "ambiguous_payslip_split",
        "multiple_employees_detected",
        "single_employee_verification_failed",
      ].includes(code),
    ),
  );
  const confirmationBlocked =
    requiresReplacement || identityNeedsAttention || periodNeedsAttention;
  const editable = Boolean(mode === "edit" && detail && !review?.confirmedAt);

  const beginEdit = (
    section: SectionKey,
    fieldName: string,
    field: PayslipField,
  ) => {
    if (!editable) return;
    setEditing({ section, field: fieldName });
    setDraftValue(
      field.effectiveValue === null || field.effectiveValue === undefined
        ? ""
        : String(field.effectiveValue),
    );
    setReason("");
    saveMutation.reset();
  };

  const inspectField = (section: SectionKey, field: string) => {
    setEditing(null);
    setAuditField({
      section,
      field,
      label: fieldLabels[field] ?? field,
    });
  };

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      size="full"
      scroll="inside"
      closeLabel="Close payslip"
      className="payslip-sheet"
    >
      <div
        className="payslip-sheet__stage"
        data-payslip-evidence="source-and-values"
        data-values-expanded={valuesExpanded}
      >
        <section
          id={`payslip-panel-${recordId}-pdf`}
          aria-label="Original payslip PDF"
          className="payslip-sheet__source overflow-hidden rounded-xl border border-border bg-[#262626]"
        >
          <header className="payslip-sheet__section-heading">
            <h2>Original payslip</h2>
            <span>Source document</span>
          </header>
          {(previewQuery.isPending || detailQuery.isPending) && (
            <p className="p-4 text-sm text-foreground-muted">
              Loading original payslip...
            </p>
          )}
          {(previewQuery.isError || detailQuery.isError) && (
            <p
              role="alert"
              className="m-4 border border-danger/40 bg-danger-subtle p-3 text-sm text-danger"
            >
              {previewQuery.error instanceof Error
                ? previewQuery.error.message
                : "The original PDF is unavailable."}
            </p>
          )}
          {previewUrl && (
            <PayslipPreview
              title={`Original payslip for ${title}`}
              imageUrl={previewUrl}
            />
          )}
        </section>
        <section
          id={`payslip-panel-${recordId}-data`}
          aria-label="Extracted payslip values"
          className="payslip-sheet__editor rounded-xl border border-border bg-surface"
        >
          <Button
            type="button"
            variant="secondary"
            className="payslip-sheet__disclosure"
            aria-expanded={valuesExpanded}
            aria-controls={`payslip-panel-${recordId}-values`}
            onClick={() => {
              setValuesExpanded((expanded) => !expanded);
              setEditing(null);
            }}
          >
            {valuesExpanded ? (
              <IconChevronUp size={17} aria-hidden="true" />
            ) : (
              <IconChevronDown size={17} aria-hidden="true" />
            )}
            Extracted values
          </Button>
          {detailQuery.isPending && (
            <p className="p-4 text-sm text-foreground-muted">
              Building extracted values...
            </p>
          )}
          {detailQuery.isError && (
            <p
              role="alert"
              className="m-4 border border-danger/40 bg-danger-subtle p-3 text-sm text-danger"
            >
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "The extracted values are unavailable."}
            </p>
          )}
          <div id={`payslip-panel-${recordId}-values`}>
            {detail && valuesExpanded && (
              <PayslipDataGrid
                detail={detail}
                editing={editing}
                editable={editable}
                onInspect={inspectField}
              />
            )}
          </div>
        </section>
        {detail && (
          <div className="payslip-sheet__action-bar border border-border bg-surface px-3 py-2">
            {editing ? (
              <>
                <label className="min-w-40 text-xs font-semibold text-foreground">
                  {fieldLabels[editing.field] ?? editing.field}
                  <input
                    autoFocus
                    value={draftValue}
                    onChange={(event) => setDraftValue(event.target.value)}
                    className="mt-1 h-9 w-full border border-border-strong bg-white px-2 text-sm text-slate-950 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="min-w-64 flex-1 text-xs font-semibold text-foreground">
                  Reason for correction
                  <input
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={500}
                    placeholder="What did you verify? (at least 15 characters)"
                    className="mt-1 h-9 w-full border border-border-strong bg-white px-2 text-sm text-slate-950 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <Button
                  type="button"
                  onClick={() => setEditing(null)}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => saveMutation.mutate()}
                  disabled={reason.trim().length < 15 || saveMutation.isPending}
                  loading={saveMutation.isPending}
                >
                  <IconDeviceFloppy size={15} />
                  Save correction
                </Button>
                {saveMutation.isError && (
                  <p role="alert" className="basis-full text-xs text-danger">
                    {payslipActionErrorMessage(saveMutation.error)}
                  </p>
                )}
              </>
            ) : editable ? (
              <>
                {valuesExpanded && (
                  <div className="flex flex-1 items-center gap-2 text-foreground-muted">
                    <IconPencil size={17} aria-hidden="true" />
                    <p className="text-sm">
                      Select a highlighted cell in the extracted values table to
                      correct it.
                    </p>
                  </div>
                )}
                {confirmationBlocked ? (
                  <div
                    role="note"
                    className="flex flex-1 items-center justify-end gap-3 text-right"
                  >
                    <div>
                      <p className="text-sm font-semibold text-warning">
                        Confirmation unavailable
                      </p>
                      <p className="text-xs text-foreground-muted">
                        {requiresReplacement
                          ? "Upload a replacement PDF before continuing."
                          : identityNeedsAttention
                            ? "Correct the payroll code before continuing."
                            : `Correct the period so it matches ${detail.month}.`}
                      </p>
                    </div>
                    {requiresReplacement && onReplace && (
                      <Button type="button" onClick={onReplace}>
                        <IconUpload size={16} aria-hidden="true" />
                        Replace PDF
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={() => confirmMutation.mutate()}
                    disabled={
                      !detail.payslipAvailable || confirmMutation.isPending
                    }
                    loading={confirmMutation.isPending}
                  >
                    <IconShieldCheck size={17} />
                    Confirm payslip
                  </Button>
                )}
                {confirmMutation.isError && (
                  <p role="alert" className="basis-full text-xs text-danger">
                    {payslipActionErrorMessage(confirmMutation.error)}
                  </p>
                )}
              </>
            ) : review?.confirmedAt ? (
              <>
                <div className="flex flex-1 items-center gap-2 text-success">
                  <IconCheck size={17} aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold">Payslip confirmed</p>
                    <p className="text-xs text-foreground-muted">
                      This employee is complete.
                    </p>
                  </div>
                </div>
                <Button type="button" onClick={onClose} variant="secondary">
                  Finish review
                </Button>
              </>
            ) : (
              <p className="text-sm text-foreground-muted">
                Read-only extracted values.
              </p>
            )}
          </div>
        )}
      </div>
      {auditField && (
        <Modal
          open
          onOpenChange={(open) => {
            if (!open) setAuditField(null);
          }}
          title="Change history"
          description={
            auditField.label + " · " + (detail?.month ?? "selected month")
          }
          size="md"
          closeLabel="Close change history"
        >
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-xl border border-accent/30 bg-accent-subtle px-3 py-3 text-sm">
              <IconHistory
                size={17}
                className="mt-0.5 shrink-0 text-accent"
                aria-hidden="true"
              />
              <p className="text-foreground-muted">
                The PDF value is the original. This list shows only manual
                changes to the selected value.
              </p>
            </div>
            {auditQuery.isPending && (
              <p className="text-sm text-foreground-muted">
                Loading audit history...
              </p>
            )}
            {auditQuery.isError && (
              <p role="alert" className="text-sm text-danger">
                Audit history could not be loaded.
              </p>
            )}
            {(auditQuery.data?.events ?? [])
              .filter(
                (event) =>
                  event.action === "payslip.field_corrected" &&
                  String(
                    event.afterPayload.field ?? event.beforePayload.field ?? "",
                  ) === auditField.field,
              )
              .map((event) => (
                <article
                  key={event.id}
                  className="rounded-xl border border-border bg-surface px-3 py-3 text-sm"
                >
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-semibold text-foreground">
                      Manual correction
                    </span>
                    <time className="text-xs text-foreground-muted">
                      {new Date(event.createdAt).toLocaleString()}
                    </time>
                  </div>
                  <p className="mt-1 text-xs text-foreground-muted">
                    {event.actorDisplayName} ·{" "}
                    {String(event.beforePayload.value ?? "Not extracted")} →{" "}
                    {String(event.afterPayload.value ?? "Not extracted")}
                  </p>
                  {event.reason && (
                    <p className="mt-2 text-xs leading-5 text-foreground-muted">
                      {event.reason}
                    </p>
                  )}
                </article>
              ))}
            {!auditQuery.isPending &&
              !auditQuery.isError &&
              (auditQuery.data?.events ?? []).filter(
                (event) =>
                  event.action === "payslip.field_corrected" &&
                  String(
                    event.afterPayload.field ?? event.beforePayload.field ?? "",
                  ) === auditField.field,
              ).length === 0 && (
                <p className="rounded-xl border border-border bg-surface px-3 py-4 text-sm text-foreground-muted">
                  No manual corrections have been recorded for this value.
                </p>
              )}
            {editable && (
              <div className="flex justify-end border-t border-border pt-3">
                <Button
                  type="button"
                  onClick={() => {
                    const field =
                      detail?.payslipDocument[auditField.section]?.[
                        auditField.field
                      ];
                    if (field)
                      beginEdit(auditField.section, auditField.field, field);
                    setAuditField(null);
                  }}
                >
                  <IconPencil size={16} aria-hidden="true" />
                  Correct value
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Modal>
  );
};

class PayslipErrorBoundary extends Component<
  PayslipSheetProps & { children: ReactNode },
  { error: Error | null }
> {
  public state: { error: Error | null } = { error: null };

  public static getDerivedStateFromError(error: Error) {
    return { error };
  }

  public render() {
    if (!this.state.error) return this.props.children;
    return (
      <Modal
        open
        onOpenChange={(open) => {
          if (!open) this.props.onClose();
        }}
        title="Payslip could not be displayed"
        closeLabel="Close payslip"
      >
        <div className="grid gap-4">
          <p role="alert" className="text-sm text-danger">
            PayDay recorded this display error in Application logs. Close this
            window and try again after the payslip data is corrected.
          </p>
          <div className="flex justify-end">
            <Button type="button" onClick={this.props.onClose}>
              Close payslip
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
}

export const PayslipSheet = (props: PayslipSheetProps) => (
  <PayslipErrorBoundary {...props} key={props.recordId}>
    <PayslipSheetContent {...props} />
  </PayslipErrorBoundary>
);
